import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { formatNumber } from "../../helpers/formatter";

/**
 * What's behind one green or red cell.
 *
 * The grid answers "are we beating him"; this answers "by how much, and how do
 * you know". Portaled to <body> because the grid scrolls in both directions
 * and anything absolute inside it gets clipped — the same technique Payroll vs
 * Revenue's own hover uses (labor.css .lbr-pop), and the same rule about
 * closing on scroll rather than pointing at empty space.
 */

const Row = ({ label, value, strong, tone }) => (
  <div className={`dm-pop-row${strong ? " strong" : ""}`}>
    <span>{label}</span>
    <b style={tone ? { color: tone } : undefined}>{value}</b>
  </div>
);
Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  strong: PropTypes.bool,
  tone: PropTypes.string,
};
Row.defaultProps = { strong: false, tone: null };

const CellPopover = ({ cell, workspace, tone, anchor, active }) => {
  /* Mounted with the entrance still "off" and flipped on the next frame, so
     adding `.in` is a real state change the CSS transition can animate from —
     otherwise it would simply appear, already open. */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const shown = mounted && active;

  /* Whole numbers, the same rounding the tiles use — the popover showed
     "$83,325.05" against a tile reading "$83,325", which makes the two look
     like different figures. Percent amounts are stored as fractions
     (0.4314 = 43.14%), so rounding the raw fraction would collapse a real rate
     to 0% or 100%; those round to the nearest whole percent instead. `|| 0`
     normalises -0 so a tiny deficit can't print "-0". */
  const fmt = (v) => {
    const n = Number(v) || 0;
    const rounded = cell.kind === "%" ? Math.round(n * 100) / 100 : Math.round(n);
    return formatNumber(rounded || 0, cell.kind);
  };

  const style = useMemo(() => {
    if (!anchor) return null;
    const W = 244;
    const H = 226;
    const GAP = 8;
    /* Prefer opening BELOW the cell — the grid grows downward, so the rows
       under a cell are usually still on screen while the ones above it may
       have scrolled under the sticky header. Flip up near the bottom edge. */
    const below = anchor.bottom + GAP + H < window.innerHeight;
    const top = below ? anchor.bottom + GAP : Math.max(8, anchor.top - H - GAP);
    const left = Math.max(8, Math.min(anchor.left + anchor.width / 2 - W / 2, window.innerWidth - W - 8));
    return { top, left, width: W };
  }, [anchor]);

  if (!style) return null;

  /* Three-state, like everywhere else this card reads `beating`: yes, no, and
     "this metric has no pace to beat at all". */
  let verdict = "No pace set";
  let verdictKey = "flat";
  if (cell.beating === true) { verdict = "Beating the Yeti"; verdictKey = "good"; }
  else if (cell.beating === false) { verdict = "Behind the Yeti"; verdictKey = "bad"; }

  const signed = (v) => `${v > 0 ? "+" : ""}${fmt(v)}`;

  return createPortal(
    <div className={`dm-pop${shown ? " in" : ""}`} style={style}>
      <div className="dm-pop-head">
        <span className="dm-pop-ws">{workspace}</span>
        <span className="dm-pop-name">{cell.name}</span>
      </div>
      <div className={`dm-pop-verdict ${verdictKey}`}>
        {verdict}
      </div>
      <div className="dm-pop-rows">
        <Row label="This team" value={fmt(cell.playerAmt)} strong />
        <Row label="Yeti's pace" value={cell.yetiAmt ? fmt(cell.yetiAmt) : "—"} />
        <Row
          label={cell.lower ? "Under by" : "Ahead by"}
          value={cell.yetiAmt ? signed(cell.lower ? -cell.diff : cell.diff) : "—"}
          tone={tone.ink}
        />
        {cell.target > 0 && <Row label="Monthly goal" value={fmt(cell.target)} />}
      </div>
      <div className="dm-pop-note">
        {/* Which of the two comparisons decided the colour. Without this a
            cell judged on amount looks like it disagrees with the points on
            the Yeti card, and there is no way to tell why. */}
        {cell.basis === "points" && (
          <>Judged on points: <b>{Math.round(cell.playerPts)}</b> against the Yeti&apos;s <b>{Math.round(cell.yetiPts)}</b>.</>
        )}
        {cell.basis === "amount" && (
          <>The Yeti scores no points on this metric for this period, so it is judged on the amount against his pace.</>
        )}
        {cell.basis === null && (
          <>This metric has no target or points rule, so there is no Yeti pace to beat — it is shown for reference only.</>
        )}
        {cell.lower && <> Lower is better here.</>}
      </div>
    </div>,
    document.body,
  );
};

CellPopover.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  cell: PropTypes.object.isRequired,
  workspace: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  tone: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  anchor: PropTypes.object,
  active: PropTypes.bool,
};
CellPopover.defaultProps = { anchor: null, active: false };

export default CellPopover;
